import { useEffect, useState } from "react";

import Image from "@Components/Image";
import InfoIcon from "@Icons/InfoIcon";
import useIsDesktop from "hooks/useIsDesktop";
import getLocation from "@Pages/Search/components/SearchListItem/helper";
import getImagePath from "@Utils/getImagePath";

import styles from "./InventoryCard.module.scss";
import { useHistory } from "hooks/useHistory";
import SealIcon from "@Icons/SealIcon";
import useAccomodationCard from "../AccomodationCard/useAccomodationCard";
import useTranslationHook from "hooks/useTranslationHook";
import wrapperHOC from "@Utils/wrapperHOC";
import getMoveInTitleLabel from "@Components/FormLayout/utils/getMoveInLabel";
import { getFeaturedImageForCard } from "@Components/FormLayout/utils";
import getParsedLocalStorageData from "@Components/LeadFormModal/helpers/getParsedLocalStorageData";
import useWhitelabel from "hooks/useWhitelabel";
import { convertToWhitelabelURL } from "@Pages/WhitelabelHome/Desktop/helpers/WhiteLabelRedirectionHelper";
import config from "@Config/index";
import { renderChinaNamedDate } from "@Utils/cnDisplayDate";
import InventoryPaymentSideCard from "../InventoryPaymentSideCard";
import PriceSummaryWrapper from "./components/PriceSummaryWrapper";

const InventoryCard = ({
  isSoldOut,
  inventory,
  isConfirmation,
  onlyView,
  isPaymentPage = false,
  bookFormPrefix = "",
  overrideSummary,
  useOverrideSummaryOnly = false,
  showInventoryPriceCard = false,
  PaymentSummaryData = {},
  isPaymentSuccess,
  paymentActive,
  compact = false,
  locationLabel,
}: {
  isSoldOut: boolean;
  inventory: any;
  isConfirmation?: boolean;
  onlyView?: boolean;
  isPaymentPage?: boolean;
  bookFormPrefix?: string;
  overrideSummary?: Partial<{
    roomName: string;
    duration: string;
    moveInDate: string;
    moveOutDate: string;
    price: string | null;
  }>;
  useOverrideSummaryOnly?: boolean;
  showInventoryPriceCard?: boolean;
  PaymentSummaryData?: any;
  isPaymentSuccess?: any;
  paymentActive?: boolean;
  compact?: boolean;
  locationLabel?: string;
}) => {
  const [featuredImage, setFeaturedImage] = useState("");
  const isDesktop = useIsDesktop();
  const history = useHistory();
  const { inventoryCardSummary: inventoryCardSummaryFromHook, accommodationDetails } =
    useAccomodationCard(true, null, null, null, false, !onlyView, isPaymentPage);
  const { isWhitelabel, isDemoSubdomain, partner_uuid } = useWhitelabel();

  const localBookFormData: any = getParsedLocalStorageData("BOOK_FORM");

  const handleClick = (e: React.MouseEvent) => {
    if (compact) return;
    e.stopPropagation();
    let link = `/places/${inventory.canonical_name}`;
    if (isWhitelabel) {
      link = convertToWhitelabelURL(link, isDemoSubdomain, partner_uuid);
    }
    history.push(link);
  };

  const inventoryCardSummary = {
    ...inventoryCardSummaryFromHook,
    ...localBookFormData?.inventoryCardSummary,
    price: inventoryCardSummaryFromHook?.price || localBookFormData?.inventoryCardSummary?.price,
    ...(overrideSummary || {}),
  };
  const { t } = useTranslationHook();
  const durationText = t("bookform.formlayout.inventorycard.duration");
  const soldOutText = t("bookform.formlayout.inventorycard.soldout");
  const roomTypeText = t("bookform.formlayout.inventorycard.roomtype");
  const getMoveInLabel = () => getMoveInTitleLabel(t, inventoryCardSummary?.moveInDate);
  const moveOutText = t("bookform.formlayout.inventorycard.moveout");
  const rentText = t("bookform.formlayout.inventorycard.rent");
  // const moveInDateText = t("Move-in Date");
  // const isNotConfirmedYetText = t("is not confirmed yet!");

  const [number, weekText] = inventoryCardSummary?.duration?.split(" ") || [];

  const hasCompactSummary = Boolean(
    overrideSummary?.roomName ||
      overrideSummary?.moveInDate ||
      overrideSummary?.moveOutDate ||
      overrideSummary?.duration ||
      overrideSummary?.price,
  );
  const shouldShowTenancy = compact
    ? hasCompactSummary
    : Boolean(useOverrideSummaryOnly ? overrideSummary?.roomName : inventoryCardSummary?.roomName);

  const getSelectedImage = () => {
    const { roomName } = inventoryCardSummary || {};
    setFeaturedImage(getFeaturedImageForCard({ inventoryData: inventory, roomName }));
  };

  useEffect(() => {
    getSelectedImage();
  }, [inventoryCardSummary]);

  if (!isDesktop) {
    return (
      <div className={styles.inventoryCardSectionMobile} onClick={handleClick} role="none">
        <div className={styles.upperSection}>
          {isSoldOut && (
            <div className={styles.soldOutTag}>
              <div>{soldOutText}</div>
            </div>
          )}
          <Image
            src={getImagePath(featuredImage)}
            className={styles.invImage}
            width="100%"
            height="auto"
          />
          <div className={styles.inventoryInfo}>
            <div className={styles.inventoryTitle}>{inventory?.name}</div>
            <div className={styles.inventorySubTitle}>
              {locationLabel || getLocation(inventory?.location)}
            </div>
          </div>
        </div>
        {inventoryCardSummary &&
          Object.keys(inventoryCardSummary).length > 0 &&
          inventoryCardSummary?.roomName && (
            <>
              <div className={styles.divider} />
              <div className={styles.accommodationDetailsSection}>
                {inventoryCardSummary?.roomName && (
                  <div className={styles.sectionDiv}>
                    <div className={styles.header}>{roomTypeText}</div>
                    <div className={styles.subHeader}>{inventoryCardSummary?.roomName}</div>
                  </div>
                )}
                {inventoryCardSummary?.duration && (
                  <div className={styles.sectionDiv}>
                    <div className={styles.header}>{durationText}</div>
                    <div className={styles.subHeader}>
                      {number} {t(weekText)}{" "}
                    </div>
                  </div>
                )}
                {inventoryCardSummary.moveInDate && (
                  <div className={styles.sectionDiv}>
                    <div className={styles.header}>{getMoveInLabel()}</div>
                    <div className={styles.subHeader}>
                      {config.IS_CHINA_APP
                        ? renderChinaNamedDate(
                            inventoryCardSummary?.moveInDate,
                            inventoryCardSummary?.moveInDate,
                          )
                        : inventoryCardSummary?.moveInDate}
                    </div>
                  </div>
                )}
                {inventoryCardSummary?.price && (
                  <div className={styles.sectionDiv}>
                    <div className={styles.header}>{rentText}</div>
                    <div className={styles.subHeader}>{inventoryCardSummary?.price}</div>
                  </div>
                )}
              </div>
            </>
          )}
        {Boolean(
          inventoryCardSummary?.isPending &&
            (accommodationDetails?.arrival_date || localBookFormData?.arrival_date) &&
            accommodationDetails?.roomType &&
            accommodationDetails?.duration,
        ) && (
          <div className={styles.pendingInfoTag}>
            <InfoIcon fill="#c27803" />
            <div>{t("bookform.formlayout.inventorycard.pendinginfotag")}</div>
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      <div className={`${styles.inventoryCardSection}${compact ? ` ${styles.compact}` : ""}`}>
        {!isSoldOut && !compact && (
          <div data-testid="payment-page-property-card-tag" className={styles.tag}>
            <SealIcon />
            <span className={styles.text}>
              {isConfirmation
                ? t("bookform.formlayout.inventorycard.amountpaid")
                : t("bookform.formlayout.inventorycard.bookwithconfidence")}
            </span>
          </div>
        )}

        <Image
          src={getImagePath(featuredImage)}
          role="none"
          className={styles.invImage}
          width="auto"
          height="auto"
          dataTestId={`${bookFormPrefix}-inventoryImage`}
          onClick={compact ? undefined : handleClick}
        />
        {isSoldOut && (
          <div className={styles.soldOutTag}>
            <div>{soldOutText}</div>
          </div>
        )}
        <div>
          <div className={styles.inventoryTitle} data-testid={`${bookFormPrefix}-inventoryName`}>
            {inventory?.name}
          </div>
          <div
            className={styles.inventorySubTitle}
            data-testid={`${bookFormPrefix}-inventoryLocation`}
          >
            {locationLabel || getLocation(inventory?.location)}
          </div>
        </div>
        {inventoryCardSummary &&
          Object.keys(inventoryCardSummary).length > 0 &&
          shouldShowTenancy && (
            <div className={styles.tenancySection}>
              <div className={styles.divider} />
              {inventoryCardSummary?.roomName && (
                <>
                  <div className={styles.sectionDiv}>
                    <div className={styles.header}>{roomTypeText}</div>
                    <div className={styles.subHeader} data-testid={`${bookFormPrefix}-roomType`}>
                      {inventoryCardSummary?.roomName}
                    </div>
                  </div>
                  <div className={styles.divider} />
                </>
              )}
              <div className={styles.accommodationDetailsSection}>
                {compact ? (
                  <>
                    {inventoryCardSummary?.moveInDate && (
                      <div className={styles.sectionDiv}>
                        <div className={styles.header}>{getMoveInLabel()}</div>
                        <div
                          className={styles.subHeader}
                          data-testid={`${bookFormPrefix}-movein-date`}
                        >
                          {config.IS_CHINA_APP
                            ? renderChinaNamedDate(
                                inventoryCardSummary?.moveInDate,
                                inventoryCardSummary?.moveInDate,
                              )
                            : inventoryCardSummary?.moveInDate}
                        </div>
                      </div>
                    )}
                    {inventoryCardSummary?.moveOutDate && (
                      <div className={styles.sectionDiv}>
                        <div className={styles.header}>{moveOutText}</div>
                        <div
                          className={styles.subHeader}
                          data-testid={`${bookFormPrefix}-moveout-date`}
                        >
                          {config.IS_CHINA_APP
                            ? renderChinaNamedDate(
                                inventoryCardSummary?.moveOutDate,
                                inventoryCardSummary?.moveOutDate,
                              )
                            : inventoryCardSummary?.moveOutDate}
                        </div>
                      </div>
                    )}
                    {inventoryCardSummary?.price && (
                      <div className={styles.sectionDiv}>
                        <div className={styles.header}>{rentText}</div>
                        <div className={styles.subHeader} data-testid={`${bookFormPrefix}-price`}>
                          {inventoryCardSummary?.price}
                        </div>
                      </div>
                    )}
                    {inventoryCardSummary?.duration && (
                      <div className={styles.sectionDiv}>
                        <div className={styles.header}>{durationText}</div>
                        <div
                          className={styles.subHeader}
                          data-testid={`${bookFormPrefix}-duration`}
                        >
                          {number} {t(weekText)}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {inventoryCardSummary?.duration && (
                      <div className={styles.sectionDiv}>
                        <div className={styles.header}>{durationText}</div>
                        <div
                          className={styles.subHeader}
                          data-testid={`${bookFormPrefix}-duration`}
                        >
                          {number} {t(weekText)}
                        </div>
                      </div>
                    )}
                    {inventoryCardSummary?.moveInDate && (
                      <div className={styles.sectionDiv}>
                        <div className={styles.header}>{getMoveInLabel()}</div>
                        <div
                          className={styles.subHeader}
                          data-testid={`${bookFormPrefix}-movein-date`}
                        >
                          {config.IS_CHINA_APP
                            ? renderChinaNamedDate(
                                inventoryCardSummary?.moveInDate,
                                inventoryCardSummary?.moveInDate,
                              )
                            : inventoryCardSummary?.moveInDate}
                        </div>
                      </div>
                    )}
                    {inventoryCardSummary?.moveOutDate && !useOverrideSummaryOnly && (
                      <div className={styles.sectionDiv}>
                        <div className={styles.header}>{moveOutText}</div>
                        <div
                          className={styles.subHeader}
                          data-testid={`${bookFormPrefix}-moveout-date`}
                        >
                          {config.IS_CHINA_APP
                            ? renderChinaNamedDate(
                                inventoryCardSummary?.moveOutDate,
                                inventoryCardSummary?.moveOutDate,
                              )
                            : inventoryCardSummary?.moveOutDate}
                        </div>
                      </div>
                    )}
                    {inventoryCardSummary?.price && (
                      <div className={styles.sectionDiv}>
                        <div className={styles.header}>{rentText}</div>
                        <div className={styles.subHeader} data-testid={`${bookFormPrefix}-price`}>
                          {inventoryCardSummary?.price}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        {Boolean(
          !compact &&
            inventoryCardSummary?.isPending &&
            (accommodationDetails?.arrival_date || localBookFormData?.arrival_date) &&
            accommodationDetails?.roomType &&
            accommodationDetails?.duration,
        ) && (
          <div
            data-testid="payment-page-property-move-in-not-confirm-message"
            className={styles.pendingInfoTag}
          >
            <InfoIcon fill="#c27803" />
            <div>{t("bookform.formlayout.inventorycard.pendinginfotag")}</div>
          </div>
        )}
        {!compact && (
          <PriceSummaryWrapper
            paymentActive={paymentActive}
            isDesktop={isDesktop}
            dividerClassName={styles.divider}
            buttonClassName={styles.viewPriceSummary}
            buttonTextClassName={styles.viewPriceSummaryText}
          />
        )}
      </div>
      {showInventoryPriceCard && (
        <div className={styles.priceSummarySection}>
          <InventoryPaymentSideCard
            data={PaymentSummaryData}
            isDesktop={isDesktop}
            isConfirmation={isPaymentSuccess}
            isInSideInventoryCard
          />
        </div>
      )}
    </>
  );
};

export default wrapperHOC(InventoryCard, {
  componentName: "InventoryCard-FormLayout-Global",
  showForChina: true,
});
